import { kelvinToCssHex } from './pathtracer/kelvin';

interface ChunkySliderProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  /** Suffix appended to the displayed value (e.g. "K", "%"). */
  suffix?: string;
  /** Use displayed value when it differs from internal value (e.g. percentages). */
  displayValue?: string;
  onChange: (next: number) => void;
  /** When set, replaces the lime fill with a horizontal gradient (used for color temperature). */
  gradient?: 'kelvin';
  /** Min/max for the gradient (defaults to min/max). */
  gradientRange?: [number, number];
}

export function ChunkySlider({
  label,
  value,
  min,
  max,
  step = 1,
  suffix = '',
  displayValue,
  onChange,
  gradient,
  gradientRange,
}: ChunkySliderProps) {
  const pct = ((value - min) / (max - min)) * 100;

  const renderFill = () => {
    if (gradient === 'kelvin') {
      const [gMin, gMax] = gradientRange ?? [min, max];
      const stops: string[] = [];
      const steps = 6;
      for (let i = 0; i <= steps; i++) {
        const k = gMin + ((gMax - gMin) * i) / steps;
        stops.push(`${kelvinToCssHex(k)} ${(i / steps) * 100}%`);
      }
      return (
        <div
          className="absolute inset-x-0 h-2 rounded-full"
          style={{ background: `linear-gradient(to right, ${stops.join(', ')})` }}
        />
      );
    }
    return (
      <>
        <div className="absolute inset-x-0 h-2 bg-[#2a2a2a] rounded-full ring-1 ring-inset ring-white/5" />
        <div className="absolute left-0 h-2 bg-[#B3EC49] rounded-full" style={{ width: `${pct}%` }} />
      </>
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-[11px] font-medium text-gray-300">{label}</label>
        <span className="text-[11px] font-mono text-white tabular-nums">{displayValue ?? `${value}${suffix}`}</span>
      </div>
      <div className="relative h-5 flex items-center">
        {renderFill()}
        <div
          className="absolute w-5 h-5 -translate-x-1/2 bg-[#B3EC49] rounded-full border-[3px] border-[#1E1E1E] shadow-md shadow-black/40 pointer-events-none"
          style={{ left: `${pct}%` }}
        />
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(parseFloat(e.target.value))}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          aria-label={label}
        />
      </div>
    </div>
  );
}
